import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Upload, X } from "lucide-react";
import { toast } from "sonner";
import client, { unwrap } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/StatusBadge";
import { KYC_ACCEPT, openFile, uploadVerificationDocument } from "@/lib/verificationDocs";

const EDITABLE_STATUSES = ["PENDING_VERIFICATION", "CHANGES_REQUESTED"];
const DETAIL_FIELDS = [
  { key: "gstNumber", label: "GST Number", placeholder: "e.g. 29ABCDE1234F1Z5" },
  { key: "contactPhone", label: "Contact Phone" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
];

const STATUS_COPY = {
  PENDING_VERIFICATION: "A Platform Admin is reviewing your shop. Most actions unlock once it's approved.",
  CHANGES_REQUESTED: "The Platform Admin asked for changes. Update your details or documents, then resubmit.",
  REJECTED: "Your shop registration was rejected.",
  APPROVED: "Your shop is verified.",
};

const errorMessage = (err) => err?.response?.data?.error?.message || "Something went wrong";

export default function ShopVerificationPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [details, setDetails] = useState({ gstNumber: "", contactPhone: "", address: "", city: "" });
  const [uploading, setUploading] = useState(false);

  const { data: tenant, isLoading } = useQuery({ queryKey: ["tenant-me"], queryFn: () => client.get("/tenants/me").then(unwrap) });
  const { data: history = [] } = useQuery({
    queryKey: ["tenant-verification-history"],
    queryFn: () => client.get("/tenants/me/verification-history").then(unwrap),
  });

  useEffect(() => {
    if (!tenant) return;
    setDetails({
      gstNumber: tenant.gstNumber || "",
      contactPhone: tenant.contactPhone || "",
      address: tenant.address || "",
      city: tenant.city || "",
    });
  }, [tenant]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["tenant-me"] });
    queryClient.invalidateQueries({ queryKey: ["tenant-verification-history"] });
  };

  const updateTenant = useMutation({
    mutationFn: (payload) => client.patch("/tenants/me", payload).then(unwrap),
    onSuccess: refresh,
    onError: (err) => toast.error(errorMessage(err)),
  });

  const resubmit = useMutation({
    mutationFn: () => client.patch("/tenants/me/resubmit").then(unwrap),
    onSuccess: () => {
      toast.success("Resubmitted for review");
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const documents = tenant?.verificationDocuments || [];
  const documentIds = documents.map((d) => d.id);
  const editable = EDITABLE_STATUSES.includes(tenant?.status) && hasPermission("tenant:profile_manage");
  // The latest platform decision that came with notes, e.g. what to change.
  const reviewNote = history.find((h) => h.action !== "SUBMITTED" && h.notes);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const file of files) ids.push(await uploadVerificationDocument(file));
      await updateTenant.mutateAsync({ verificationFileIds: [...documentIds, ...ids] });
      toast.success(files.length === 1 ? "Document added" : `${files.length} documents added`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (isLoading) return <p className="text-sm text-[#8C8379]">Loading…</p>;
  if (!tenant?.id) return <p className="text-sm text-[#8C8379]">No shop is linked to this account.</p>;

  return (
    <div data-testid="shop-verification-page" className="max-w-2xl">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-serif-display text-3xl text-[#2A211D]">Shop Verification</h1>
        <StatusBadge status={tenant.status} />
      </div>
      <p className="text-sm text-[#6E6259] mt-2 mb-6">{STATUS_COPY[tenant.status]}</p>

      {reviewNote && tenant.status !== "APPROVED" && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="verification-review-note">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Note from the Platform Admin</p>
          <p className="text-sm text-[#2A211D] mt-1 whitespace-pre-line">{reviewNote.notes}</p>
        </div>
      )}

      <section className="bg-white border border-[#E6DDD3] rounded-xl p-6 mb-6">
        <h2 className="font-heading text-lg text-[#2A211D] mb-4">Business details</h2>
        <form
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            updateTenant.mutate(details, { onSuccess: () => toast.success("Details saved") });
          }}
        >
          {DETAIL_FIELDS.map((f) => (
            <div key={f.key} className={f.key === "address" ? "sm:col-span-2" : ""}>
              <Label>{f.label}</Label>
              <Input
                data-testid={`verification-${f.key}-input`}
                className="mt-1.5"
                placeholder={f.placeholder}
                value={details[f.key]}
                disabled={!editable}
                onChange={(e) => setDetails((d) => ({ ...d, [f.key]: e.target.value }))}
              />
            </div>
          ))}
          {editable && (
            <div className="sm:col-span-2">
              <Button type="submit" variant="outline" disabled={updateTenant.isPending} data-testid="verification-save-details-button">
                Save details
              </Button>
            </div>
          )}
        </form>
      </section>

      <section className="bg-white border border-[#E6DDD3] rounded-xl p-6 mb-6">
        <h2 className="font-heading text-lg text-[#2A211D]">Documents</h2>
        <p className="text-sm text-[#6E6259] mt-1 mb-4">GST certificate, shop establishment proof or similar. PDF, PNG or JPEG.</p>
        <ul className="space-y-2 mb-4" data-testid="verification-documents-list">
          {documents.map((d) => (
            <li key={d.id} className="flex items-center gap-3 rounded-lg border border-[#E6DDD3] px-3 py-2">
              <FileText className="h-4 w-4 shrink-0 text-[#8C8379]" />
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-sm text-[#C85A32] hover:underline"
                onClick={() => openFile(d.id).catch((err) => toast.error(errorMessage(err)))}
              >
                {d.originalName}
              </button>
              {editable && (
                <button
                  type="button"
                  aria-label={`Remove ${d.originalName}`}
                  className="p-1 text-[#8C8379] hover:text-red-600"
                  onClick={() => updateTenant.mutate({ verificationFileIds: documentIds.filter((id) => id !== d.id) })}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
          {documents.length === 0 && <li className="text-sm text-[#8C8379]">No documents yet.</li>}
        </ul>
        {editable && (
          <>
            <input ref={fileInputRef} type="file" multiple accept={KYC_ACCEPT} className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            <Button type="button" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="verification-upload-button">
              <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Add documents"}
            </Button>
          </>
        )}
      </section>

      {tenant.status === "CHANGES_REQUESTED" && editable && (
        <Button
          className="bg-[#C85A32] hover:bg-[#B04B26] text-white rounded-full px-6"
          disabled={resubmit.isPending}
          onClick={() => resubmit.mutate()}
          data-testid="verification-resubmit-button"
        >
          Resubmit for review
        </Button>
      )}
    </div>
  );
}
